// @ts-check
import { values, filter, forEach, partition } from "lodash";

import {
  newGrouping,
  newGroupingOptions,
  buildBudgetLookup,
  getDailyRateByFlag,
} from "../../util";
import { getNearestDemandRollupTargetId } from "src/allocation/util/groupHierarchy.util";

export default ({
  rootGroup,
  hideHiddenTeams,
  showObjectives = false,
  groupTypes,
  columnCount,
  isBudgetEnabled = false,
  groupLookup,
  allocationProjectLookup,
  enableDemandRollupPlanning = false,
}) => {
  // collect all our target groups
  const hasDemandRollupTargets = values(groupTypes).some(
    (groupType) => groupType.planning?.isDemandRollupTarget
  );
  const isDemandRollupEnabled =
    enableDemandRollupPlanning && hasDemandRollupTargets;

  let setGroupingBudget = null;
  if (isBudgetEnabled) {
    setGroupingBudget = buildBudgetLookup({
      groupLookup,
      rootGroup,
      allocationProjectLookup,
    });
  }
  // should only be 1 project in allocationProjectLookup
  const activeAllocationProject = values(allocationProjectLookup).find(
    (ap) => ap.isActive
  );

  let targetGroupsYetToInsert = filter(
    values(groupLookup),
    (g) =>
      g.isDemand && (g.parentIds?.includes(rootGroup.id) || rootGroup.isSource)
  );
  let targetGroupsToInsert = [];
  const groupType = groupTypes[rootGroup.type];
  const rootGrouping = newGrouping({
    ...newGroupingOptions({
      group: rootGroup,
      groupType,
      activeAllocationProject,
    }),
    parentGrouping: null,
    rootGrouping: null,
    nestedLevel: 0,
    isRoot: true,
    canAddLineItem: !rootGroup.isSource && !rootGroup.isLineOfBusiness,
    canViewInTeamBuilder: !rootGroup.isSource && !rootGroup.isLineOfBusiness,
    canViewInDetails: false,
    showObjectives,
    columnCount,
    dailyRate: getDailyRateByFlag(
      rootGroup.isSource && isBudgetEnabled, // Only set for supply rootGroup
      rootGroup,
      null,
      groupLookup
    ),
  });
  rootGrouping.root = rootGrouping;
  const groupingsLookup = { [rootGrouping.id]: rootGrouping };

  // insert the groups in order from top most hierarchy level to lowest.
  for (let hierarchyLevel = 0; hierarchyLevel < 999; hierarchyLevel += 1) {
    [targetGroupsToInsert, targetGroupsYetToInsert] = partition(
      targetGroupsYetToInsert,
      (t) => t.parentIds.length === hierarchyLevel
    );

    forEach(targetGroupsToInsert, (targetGroup) => {
      if (hideHiddenTeams && (targetGroup.isHidden || targetGroup.isDeleted)) {
        return;
      }
      // console.log("targetGroupsToInsert", targetGroupsToInsert);
      let parentGrouping = groupingsLookup[targetGroup.directParentId];
      if (!parentGrouping) {
        if (rootGroup.isSource) {
          parentGrouping = rootGrouping;
        } else {
          console.warn(
            `ignoring group '${targetGroup.id}'. Cannot find not parent '${targetGroup.directParentId}'`
          );
          return;
        }
      }
      // console.log("parentGrouping", parentGrouping);
      const rollupTargetId = isDemandRollupEnabled
        ? getNearestDemandRollupTargetId({
            group: targetGroup,
            groupLookup,
            groupTypes,
          })
        : null;
      const isDemandRollupContext =
        Boolean(rollupTargetId) && rollupTargetId !== targetGroup.id;
      const canEditAllocationRows =
        !isDemandRollupEnabled || !isDemandRollupContext;

      const grouping = newGrouping({
        ...newGroupingOptions({
          group: targetGroup,
          groupType: groupTypes[targetGroup.type],
          parentGrouping,
          activeAllocationProject,
          isBudgetEnabled,
          groupLookup,
        }),
        parentGrouping,
        rootGrouping,
        nestedLevel: parentGrouping.nestedLevel + 1,
        canAddLineItem:
          canEditAllocationRows &&
          !rootGroup.isSource &&
          !rootGroup.isLineOfBusiness,
        canViewInTeamBuilder:
          canEditAllocationRows &&
          !rootGroup.isSource &&
          !rootGroup.isLineOfBusiness,
        canViewInDetails: true,
        canAllocate: canEditAllocationRows,
        isDemandRollupContext,
        showObjectives,
        columnCount,
      });

      if (isBudgetEnabled) {
        setGroupingBudget(grouping, "isSupply");
      }

      parentGrouping.childGroupings.push(grouping);
      // TODO: perf improvement - insert child into correct position, instead of sorting everything.
      parentGrouping.childGroupings.sort((a, b) =>
        a.group.name.localeCompare(b.group.name)
      );
      groupingsLookup[grouping.id] = grouping;
    });

    if (targetGroupsYetToInsert.length === 0) {
      break;
    }
  }

  return [rootGrouping];
};
